import { jsx as _jsx } from "react/jsx-runtime";
/**
 * 文档身份管理上下文
 *
 * 本模块提供React组件访问和管理文档身份的能力，是组件获取和操作文档ID的核心机制。
 *
 * 重要: 在使用此上下文前，请先阅读文档ID生命周期指南:
 * @see ../DOCUMENT_ID_LIFECYCLE.md
 *
 * 主要功能：
 * 1. 提供DocumentIdentityProvider上下文提供者组件
 * 2. 导出访问文档ID和操作的钩子函数
 * 3. 提供统一的ID验证机制
 *
 * 推荐使用方式：
 * - 简单组件：使用useDocumentControl钩子（从document/index.ts导入）
 * - 需要直接访问上下文的组件：使用useDocumentIdentity钩子
 * - 需要验证文档ID的场景：使用useIsValidDocumentId钩子
 */
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { DocumentIdentitySource, } from '@readsavor/types';
import { DocumentIdentityService } from '../services/DocumentIdentityService.js';
import { LoggerService } from '../../logging/services/logger.service.js';
// 获取日志服务实例
const logger = LoggerService.getInstance();
// 创建上下文
const DocumentIdentityContext = createContext(undefined);
/**
 * 文档身份提供者组件
 *
 * 为应用提供文档身份管理功能，通常在应用顶层使用，确保所有子组件能访问文档ID。
 */
export const DocumentIdentityProvider = ({ children, initialIdentity, initialSource = DocumentIdentitySource.PROPS, onIdentityChange, }) => {
    const service = useMemo(() => {
        const serviceInstance = DocumentIdentityService.getInstance();
        let effectiveInitialState = {};
        if (initialIdentity && serviceInstance.validateIdentity(initialIdentity)) {
            effectiveInitialState = {
                currentIdentity: initialIdentity,
                documentSource: initialSource,
                isValidIdentity: true,
                isActive: true,
            };
        }
        return DocumentIdentityService.getInstance(effectiveInitialState);
    }, [initialIdentity, initialSource]);
    useEffect(() => {
        if (initialIdentity?.id !== undefined && initialIdentity?.type !== undefined) {
            if (service.validateIdentity(initialIdentity)) {
                service.activateDocument(initialIdentity, initialSource);
            }
        }
    }, [initialIdentity, initialSource, service]);
    const instanceId = useMemo(() => `docIdentity_${Math.random().toString(36).substring(2, 9)}`, []);
    const [state, setState] = useState(() => service.state);
    useEffect(() => {
        logger.debug(`[DocumentIdentityProvider ${instanceId}] 初始化`, {
            data: { initialIdentity, initialSource },
        });
        const removeListener = service.addIdentityChangeListener((event) => {
            const newServiceState = service.state;
            setState(prevState => {
                const prevId = prevState.currentIdentity?.id;
                const newId = newServiceState.currentIdentity?.id;
                const prevType = prevState.currentIdentity?.type;
                const newType = newServiceState.currentIdentity?.type;
                if (prevId !== newId ||
                    prevType !== newType ||
                    prevState.isValidIdentity !== newServiceState.isValidIdentity ||
                    prevState.documentSource !== newServiceState.documentSource) {
                    return newServiceState;
                }
                return prevState;
            });
            if (onIdentityChange) {
                onIdentityChange(event);
            }
            logger.debug(`[DocumentIdentityProvider ${instanceId}] 文档身份事件`, {
                data: {
                    eventType: event.type,
                    identity: event.identity,
                    previousIdentity: event.previousIdentity,
                    source: event.source,
                },
            });
        });
        return () => {
            logger.debug(`[DocumentIdentityProvider ${instanceId}] 卸载`, {
                data: {
                    currentIdentity: service.state.currentIdentity,
                },
            });
            removeListener();
        };
    }, [service, onIdentityChange, instanceId, initialIdentity, initialSource]);
    const activateDocument = useCallback((identity, source, metadata) => service.activateDocument(identity, source, metadata), [service]);
    const deactivateDocument = useCallback((identity) => {
        if (identity === undefined) {
            return service.deactivateDocument();
        }
        else {
            return service.deactivateDocument(identity);
        }
    }, [service]);
    const validateIdentity = useCallback((identity) => service.validateIdentity(identity), [service]);
    const contextValue = useMemo(() => ({
        currentIdentity: state.currentIdentity,
        isValidIdentity: state.isValidIdentity,
        documentSource: state.documentSource,
        activateDocument,
        deactivateDocument,
        validateIdentity,
    }), [
        state.currentIdentity,
        state.isValidIdentity,
        state.documentSource,
        activateDocument,
        deactivateDocument,
        validateIdentity,
    ]);
    return (_jsx(DocumentIdentityContext.Provider, { value: contextValue, children: children }));
};
/**
 * 使用文档身份钩子
 *
 * 直接访问文档身份上下文的钩子函数，用于需要访问和操作文档ID的组件。
 * 注意：此钩子需要在DocumentIdentityProvider内部使用，否则会抛出错误。
 *
 * 对于大多数组件，建议使用更高级的useDocumentControl钩子，它提供了更完善的功能。
 *
 * @returns DocumentIdentityContextType 文档身份上下文值
 * @throws Error 如果在DocumentIdentityProvider之外使用
 */
export function useDocumentIdentity() {
    const context = useContext(DocumentIdentityContext);
    if (context === undefined) {
        throw new Error('useDocumentIdentity must be used within a DocumentIdentityProvider');
    }
    return context;
}
/**
 * 使用可选文档身份钩子
 *
 * 与useDocumentIdentity类似，但在DocumentIdentityProvider外部使用时不会抛出错误，
 * 而是返回undefined。主要用于库组件或不确定是否在提供者内部的组件。
 *
 * @returns DocumentIdentityContextType | undefined 文档身份上下文值，或在提供者外部时为undefined
 */
export function useOptionalDocumentIdentity() {
    return useContext(DocumentIdentityContext);
}
/**
 * 检查文档身份是否有效的辅助钩子 (原 useIsValidDocumentId)
 *
 * 此钩子是React组件中验证文档身份的标准方式。
 * 当上下文可用时，它使用上下文提供的验证方法。
 * 当上下文不可用时，它执行静态的宽松验证（ID格式和Type是否已知）。
 *
 * @param identity 要验证的文档身份
 * @returns boolean 文档身份是否有效（或格式上宽松有效）
 */
export function useIsValidIdentity(identity) {
    const context = useOptionalDocumentIdentity();
    if (context) {
        return context.validateIdentity(identity);
    }
    if (!identity || identity.id === null || isNaN(identity.id) || identity.id <= 0) {
        return false;
    }
    if (!identity.type || !['text', 'pdf'].includes(identity.type)) {
        return false;
    }
    return true;
}
//# sourceMappingURL=DocumentIdentityContext.js.map